import { DotLegalFullScreenSpinner, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { convertEnumToStringOrNull } from "@common/enumOperations";
import { useTranslation } from "@localization/useTranslation";
import TagsMultiSelect from "@common/components/tagsMultiSelect/TagsMultiSelect";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import { usePlanContext } from "../../auth/planProvider/PlanProvider.tsx";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { useResponsibleQuery } from "../../user/hooks/useResponsibleQuery.ts";
import { usePolicyQuery } from "../policyOverview/PolicyOverview.hooks";
import PaneLayout from "../../main/panes/paneLayout/PaneLayout";
import { definePane } from "../../main/panes/definePane";
import { useAddEditPolicyPane } from "./AddEditPolicyPane.hooks";
import { AddEditPolicyPaneProps, PolicyPaneFormProps } from "./AddEditPolicyPane.types";

function AddEditPolicyPane(props: AddEditPolicyPaneProps) {
    if (props.policyId) {
        return <EditPolicyPane policyId={props.policyId} />;
    }

    return <PolicyPaneForm />;
}

function EditPolicyPane(props: { policyId: string }) {
    const { translateString } = useTranslation();
    const { data } = usePolicyQuery(props.policyId);

    if (!data) {
        return (
            <PaneLayout header={translateString("editPolicy")}>
                <DotLegalFullScreenSpinner />
            </PaneLayout>
        );
    }

    return <PolicyPaneForm policyId={props.policyId} policy={data} />;
}

function PolicyPaneForm(props: PolicyPaneFormProps) {
    const { translateString } = useTranslation();
    const { saveModel, onModelChange, savePolicy, validation, typeOptions, isEdit, isSaving } = useAddEditPolicyPane(props);
    const { permissions } = useUserContext();
    const { addonsFeatures } = usePlanContext();

    const hasPolicyManagement = addonsFeatures.policyManagement;

    const responsibleQuery = useResponsibleQuery();

    validation?.check(saveModel);

    return (
        <PaneLayout
            reserveFieldTooltipSpace
            header={isEdit ? translateString("editPolicy") : translateString("createPolicy")}
            okButtonText={isEdit ? translateString("save") : translateString("create")}
            onOkClick={savePolicy}
            inProgress={isSaving}
        >
            <DotLegalTextField
                debounce={false}
                size="small"
                label={translateString("name")}
                value={saveModel.name}
                errorText={validation?.getErrorsForDisplay("name")}
                onChange={(name) => {
                    onModelChange({ ...saveModel, name });
                }}
                autoFocus
            />
            <DotLegalTextField
                debounce={false}
                size="small"
                label={translateString("description")}
                value={saveModel.description}
                onChange={(description) => {
                    onModelChange({ ...saveModel, description });
                }}
                multiline
                rows={3}
            />
            <DotLegalSelect
                label={translateString("type")}
                options={typeOptions}
                placeholder={""}
                disableClearable
                errorText={validation?.getErrorsForDisplay("type")}
                selectedItem={convertEnumToStringOrNull(saveModel.type)}
                onChange={(type) => {
                    onModelChange({ ...saveModel, type: type !== null ? Number(type) : undefined });
                }}
                noOptionsLabel={translateString("noOptions")}
            />
            <DotLegalSelect
                label={translateString("responsible")}
                options={responsibleQuery.data(saveModel.responsibleId)}
                isLoading={responsibleQuery.isLoading}
                selectedItem={saveModel.responsibleId}
                placeholder={""}
                disableClearable
                errorText={validation?.getErrorsForDisplay("responsibleId")}
                onChange={(responsibleId) => {
                    onModelChange({ ...saveModel, responsibleId });
                }}
                noOptionsLabel={translateString("noOptions")}
            />
            {hasPolicyManagement && (
                <DotLegalSelect
                    label={translateString("approver")}
                    options={responsibleQuery.data(saveModel.approverId)}
                    isLoading={responsibleQuery.isLoading}
                    selectedItem={saveModel.approverId}
                    placeholder={""}
                    disableClearable
                    errorText={validation?.getErrorsForDisplay("approverId")}
                    onChange={(approverId) => {
                        onModelChange({ ...saveModel, approverId });
                    }}
                    noOptionsLabel={translateString("noOptions")}
                    toolTipText={translateString("policyApproverInfo")}
                />
            )}

            {permissions.canAccessTags && (
                <TagsMultiSelect
                    entityType={TagRelationType.Policy}
                    onChange={(tags) => {
                        onModelChange({ ...saveModel, tags });
                    }}
                    isLoading={false}
                    selectedTags={saveModel.tags}
                />
            )}
        </PaneLayout>
    );
}

export default definePane({ slot: "right", component: AddEditPolicyPane });
